import { createTheme } from '@mui/material/styles';
import { CHAT_ITEM_HEIGHT } from './components/Nav/RecentChatsSection/chatListMetrics';

declare module '@mui/material/styles' {
  interface Palette {
    buttons: string;
    accent1: string;
    accent2: string;
    accent3: string;
    hover: string;
    active: string;
    stroke: string;
    'nav-bar': string;
  }
  interface PaletteOptions {
    buttons: string;
    accent1: string;
    accent2: string;
    accent3: string;
    hover: string;
    active: string;
    stroke: string;
    'nav-bar': string;
  }

  interface TypeText {
    inactive: string;
  }
  interface TypeBackground {
    modal: string;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'nav-button': true;
    'chat-item': true;
  }
  interface ButtonPropsColorOverrides {
    active: true;
    accent1: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    'on-video': true;
  }
}

export const appTheme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#ffffff',
      contrastText: '#000000',
    },
    secondary: {
      main: '#000000',
      contrastText: '#ffffff',
    },
    background: {
      default: '#000000',
      paper: '#1c1c1c',
      modal: 'rgba(30, 30, 30, 0.95)',
    },
    text: {
      primary: '#ececec',
      disabled: '#c1c1c2',
      inactive: 'rgba(193, 193, 194, 1)',
    },
    accent1: '#9d4edd',
    accent2: '#4fd1d9',
    accent3: '#d64550',

    buttons: 'rgba(255, 255, 255, 0.08)',
    hover: 'rgba(255, 255, 255, 0.15)',

    // used for active state of nav buttons
    active: 'rgba(255, 255, 255, 0.20)',

    stroke:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.30),rgba(255, 255, 255, 0.00001), rgba(255, 255, 255, 0.00001), rgba(255, 255, 255, 0.10))',

    'nav-bar': 'rgba(255, 255, 255, 0.08)',
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
          background: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          height: '40px',
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            color: 'var(--mui-palette-text-inactive)',
          },
        },
        {
          props: { variant: 'nav-button' },
          style: {
            borderRadius: '28px',
            // Matches the chat frontend's nav-button variant (monorepo.vibedit
            // theme.ts: 40px / 8px 20px). Ours was 56px / 1em and only rendered
            // at 40px because Nav.module.scss forced it with `!important`; align
            // the theme so the rail height no longer depends on that override.
            padding: '8px 20px 8px 20px',
            height: '40px',
            minWidth: '56px',
            width: '100%',
            justifyContent: 'start',
            textTransform: 'none',
            color: 'var(--mui-palette-text-primary)',

            '& p': {
              marginLeft: '1em',
            },
            // '& svg': {
            //     marginRight: 'auto'
            // }

            backgroundColor: 'unset',
          },
        },
        {
          props: { variant: 'nav-button', color: 'active' },
          style: {
            backgroundColor: 'var(--mui-palette-active)',
          },
        },
        {
          // Chat-list row (Starred / Recents). A real button (ripple + keyboard
          // activation + role=button) that must LOOK like the old flat div row:
          // transparent at rest, a faint wash on hover. Without this variant the
          // global MuiButtonBase override paints it a solid 0.08 pill with a 0.15
          // hover. Layout (height, kebab reveal, ellipsis) stays in
          // RecentChatsSection.module.scss via the `.item` class.
          props: { variant: 'chat-item' },
          style: {
            width: '100%',
            height: `${CHAT_ITEM_HEIGHT}px`,
            minWidth: 0,
            padding: '6px 8px 6px 12px',
            gap: '6px',
            borderRadius: '8px',
            justifyContent: 'flex-start',
            textAlign: 'left',
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: 'normal',
            color: 'var(--mui-palette-text-primary)',
            backgroundColor: 'transparent',
            transition: 'background-color 0.1s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
        {
          props: { color: 'accent1' },
          style: {
            backgroundColor: 'var(--mui-palette-accent1)',
            color: 'var(--mui-palette-text-primary)',
          },
        },
        {
          props: { variant: 'text' },
          style: {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
            textDecoration: 'none',
          },
        },
      ],
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          background: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          background: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          background: 'none',
          color: 'var(--mui-palette-text-inactive)',
          '&:hover': {
            color: 'var(--mui-palette-text-primary)',
          },
          '&:disabled': {
            filter: 'saturate(0)',
          },
        },
      },
      variants: [
        {
          props: { color: 'on-video' },
          style: {
            color: 'var(--mui-palette-primary-main)',
            '&:hover': {
              color: 'var(--mui-palette-primary-main)',
            },
          },
        },
      ],
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          background: 'none',
          '& .MuiButtonBase-root': {
            background: 'none',
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          '& .MuiPaper-root': {
            background: 'var(--mui-palette-background-modal)',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            background: 'var(--mui-palette-background-modal)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'var(--mui-palette-text-primary)',
          color: 'var(--mui-palette-secondary-main)',
        },
        arrow: {
          color: 'var(--mui-palette-text-primary)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: 'filled' },
          style: {
            background: 'var(--mui-palette-background-paper)',
          },
        },
      ],
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'var(--mui-palette-background-paper)',
          '&:hover': {
            background: 'var(--mui-palette-hover)',
          },
          borderRadius: 8,
          '& .MuiAccordionDetails-root': {
            background: 'var(--mui-palette-background-paper)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          margin: '0 0.5em',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '36px',
          backgroundColor: 'rgba(22, 22, 22, 1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
        select: {
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
        },
        icon: {
          color: 'var(--mui-palette-text-primary)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        // Frosted dark menu surface. Consolidated here from the old SortSelect
        // global styles so every menu (Filters, Sort, context menus) is themed in
        // one place. The frosted bg/blur + drop shadow previously won via !important.
        paper: {
          backgroundColor: 'rgba(30, 30, 30, 0.85)',
          backdropFilter: 'blur(100px)',
          boxShadow: '0px 24px 32px -12px rgba(18, 18, 18, 0.1)',
          borderRadius: '12px',
          marginTop: '4px',
        },
        list: {
          padding: '4px',
          // Divider styling scoped to menus only — applying it via a global
          // MuiDivider.root override would add 8px of vertical margin to every
          // standalone <Divider> in the app (Settings, Moment modals, etc.).
          '& .MuiDivider-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            height: '1px',
            marginTop: '4px',
            marginBottom: '4px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        // Compact menu rows (also consolidated from the old SortSelect globals).
        root: {
          width: '199px',
          height: '36px',
          padding: '0 8px',
          borderRadius: '6px',
          backgroundColor: 'transparent',
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
    },
  },
});
